import React, { useState, useEffect, useCallback } from "react";
import Select from "react-select";
import Layout from "../Component/Layout";
import CharacteristicsModal from "../Component/CharacteristicsModal ";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { MASTER_API, ORDER_API } from "../apiConfig";
import { FileText } from 'lucide-react';
import { authHeaders } from "../utils/authHeaders";

// Logged-in user's ID (same logic as ProductionOrder.js) — sent with every MRP
// run so the backend can save it in planning_elements.created_by
const getCurrentUser = () => {
  const userObj = localStorage.getItem("user");
  if (userObj) {
    try {
      const parsedUser = JSON.parse(userObj);
      if (parsedUser.userId) return parsedUser.userId;
    } catch (e) { console.error("Error parsing user data:", e); }
  }
  const userId = localStorage.getItem("userId");
  if (userId && userId !== "undefined" && userId !== "null") return userId;
  return "";
};

/* ===================== STYLES ===================== */

const tableHeader = {
  background: "#f1f1f1",
  padding: "10px 8px",
  borderBottom: "1px solid #ccc",
  textAlign: "center",
  fontSize: "12px",
  fontWeight: 600,
  color: "#555",
  whiteSpace: "nowrap"
};

const tableCell = {
  padding: "10px 8px",
  borderBottom: "1px solid #eee",
  fontSize: "13px",
  textAlign: "center",
  color: "#444",
  wordBreak: "break-word",
  whiteSpace: "normal"
};

const tabButton = (active) => ({
  padding: "8px 14px",
  border: "none",
  background: "transparent",
  borderBottom: active ? "3px solid #1976d2" : "3px solid transparent",
  cursor: "pointer",
  fontWeight: active ? 700 : 500,
  fontSize: 13
});

const runButtonStyle = {
  padding: "6px 14px",
  background: "#1976d2",
  color: "#fff",
  border: "none",
  borderRadius: 4,
  cursor: "pointer",
  fontSize: 13,
  fontWeight: 600
};

// Matches Pegging.js dropdown look
const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 32,
    height: 32,
    fontSize: "13px",
    borderColor: "#dee2e6",
    boxShadow: "none",
    "&:hover": { borderColor: "#b3b3b3" },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 8px",
    height: "32px",
    display: "flex",
    alignItems: "center",
  }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  menu: (base) => ({ ...base, fontSize: "13px", zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#1e293b"
      : state.isFocused
        ? "#f1f5f9"
        : "white",
    color: state.isSelected ? "white" : "#334155",
    cursor: "pointer",
    fontSize: "13px",
  }),
};

// Matches Pegging.js page title / filter label / filter card styling
const pageTitle = { fontSize: 20, fontWeight: 700, color: "#1e293b", margin: 0 };

const filterLabel = {
  fontSize: 12,
  fontWeight: 600,
  marginBottom: 6,
  color: "#334155",
};

const filterCard = {
  background: "#ffffff",
  borderRadius: 8,
  padding: 15,
  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
  marginBottom: 18,
};

// Matches Pegging.js compact filter row styling
const compactSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 30,
    fontSize: "12px",
    borderColor: "#dee2e6",
    boxShadow: "none",
    "&:hover": { borderColor: "#b3b3b3" },
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 6px", flexWrap: "wrap" }),
  input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: "12px" }),
  indicatorsContainer: (base) => ({ ...base, alignSelf: "flex-start", paddingTop: 2 }),
  dropdownIndicator: (base) => ({ ...base, padding: 2 }),
  menu: (base) => ({ ...base, fontSize: "12px", zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#1e293b"
      : state.isFocused
        ? "#f1f5f9"
        : "white",
    color: state.isSelected ? "white" : "#334155",
    cursor: "pointer",
    fontSize: "12px",
  }),
};

const compactFilterBox = {
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: 8,
  padding: "10px 12px",
  marginBottom: 14,
};

const compactFilterItemLabel = {
  fontSize: 11,
  fontWeight: 600,
  color: "#64748b",
  whiteSpace: "nowrap",
  letterSpacing: "0.02em",
  paddingLeft: 2,
};

/* ===================== FILTER CONFIG (matches Pegging.js) ===================== */
const FILTER_FIELDS = [
  { label: "Product Type", key: "productType", dataKey: "productTypes", endpoint: "product-types" },
  { label: "Category", key: "category", dataKey: "categories", endpoint: "dynamic/category" },
  { label: "Cable Type", key: "cableType", dataKey: "cableTypes", endpoint: "cable-type" },
  { label: "Code", key: "code", dataKey: "codes", endpoint: "dynamic/code" },
  { label: "Color", key: "color", dataKey: "colours", endpoint: "colour" },
  { label: "Cores", key: "noOfCores", dataKey: "cores", endpoint: "core" },
  { label: "Cross Section", key: "nominalCrossSection", dataKey: "nominalCrossSections", endpoint: "dynamic/nominal-cross-section" },
  { label: "Std. Ref", key: "referenceOfStandard", dataKey: "referenceOfStandards", endpoint: "dynamic/reference-of-standard" },
  { label: "Voltage", key: "voltage", dataKey: "voltages", endpoint: "dynamic/voltage" },
  { label: "Construction", key: "construction", dataKey: "constructions", endpoint: "construction" },
];

const USER_MANUAL_SCOPE = {
    moduleName: "Planning",
    process: "Planning",
    subProcess: "Product Planning"
};
const USER_MANUAL_LIST_API = `${MASTER_API}/user-manual/list`;

function ProductPlanning() {

  const location = useLocation();
  const navigate = useNavigate();

  const [showManual, setShowManual] = useState(false);
  const [manualExists, setManualExists] = useState(null);
  const [manualFile, setManualFile] = useState(null);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [activeTab, setActiveTab] = useState("elements");
  const [scheduleModal, setScheduleModal] = useState(false);
  const [mrpRunning, setMrpRunning] = useState(false);
  const [peggingRunning, setPeggingRunning] = useState(false);
  const [autoMrpRunning, setAutoMrpRunning] = useState(false);
  const [autoMrpScope, setAutoMrpScope] = useState("all"); // "all" | "product" | "process" | "allProcess"
  const [autoMrpProduct, setAutoMrpProduct] = useState(null); // product picked INSIDE the modal, independent of the page's selectedProduct
  const [autoMrpProcess, setAutoMrpProcess] = useState(null); // process picked INSIDE the modal, independent of the page's filterValues.processId
  const [autoMrpFrequency, setAutoMrpFrequency] = useState("immediate"); // "immediate" | "hourly" | "daily" | "weekly" | "monthly"
  const [autoMrpHour, setAutoMrpHour] = useState("09:00"); // used by hourly/daily/weekly/monthly
  const [autoMrpDayOfMonth, setAutoMrpDayOfMonth] = useState(1); // used by monthly (1-28, keeps it valid for every month)
  const [autoMrpDayOfWeek, setAutoMrpDayOfWeek] = useState(1); // used by weekly (0=Sun ... 6=Sat)
  const [mrpSchedules, setMrpSchedules] = useState([]); // persisted schedules, shown + deletable inside the modal
  const [toast, setToast] = useState(null); // { message, type }

  const showToast = (message, type = "success", durationMs = 3000) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), durationMs);
  };

  const [dropdownData, setDropdownData] = useState({
    productTypes: [],
    categories: [],
    cableTypes: [],
    codes: [],
    colours: [],
    cores: [],
    nominalCrossSections: [],
    referenceOfStandards: [],
    voltages: [],
    constructions: [],
  });
  const [filterValues, setFilterValues] = useState({});
  // Filters accordion: expanded by default, user can collapse to free up screen space.
  const [filtersOpen, setFiltersOpen] = useState(true);
  const activeFilterCount =
    Object.values(filterValues).filter(Boolean).length + (selectedProduct ? 1 : 0);

  // Resets every filter (top bar + selected product) back to empty.
  const clearAllFilters = () => {
    setFilterValues({});
    setSelectedProduct(null);
    setProductDescription("");
  };
  const [processOptions, setProcessOptions] = useState([]);

  const [products, setProducts] = useState([]);
  const [productDescription, setProductDescription] = useState("");
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [stockData, setStockData] = useState([]);
  const [reorderData, setReorderData] = useState(null);
  // Note: setProductionOrders isn't called anywhere yet — productionOrders
  // stays [] until a fetch call populates it. Left as read-only for now to
  // silence the unused-var warning without changing current behavior.
  const [productionOrders] = useState([]);

  // --- Sales Order characteristics modal (Elements tab "play" button) ---
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedCharacteristics, setSelectedCharacteristics] = useState([]);

  // planningElements = flat list from planning_elements table for selected product
  const [planningElements, setPlanningElements] = useState([]);

  // peggingData = rows from pegging table for selected product (for Pegging tab)
  const [peggingData, setPeggingData] = useState([]);

  // Pagination for the Elements table — user picks how many rows per page.
  const [elementsPageSize, setElementsPageSize] = useState(25);
  const [elementsPage, setElementsPage] = useState(1);

  const productOptions = products.map((item) => ({
    value: item.productCode,
    label: item.productCode,
    description: item.productDescription
  }));

  useEffect(() => {
    fetchDropdowns();
  }, []);

  // --- Restore the previously selected product (and tab) when returning
  // from a linked page (Production Order / Planned Order / PR modal close).
  // Fire immediately on mount — don't wait for the full dropdown lists to
  // finish loading first, since fetchProductData only needs the product
  // code, not the dropdown-derived option object. This is what was making
  // "return to planning" feel slow: it used to sit idle until every filter
  // dropdown had loaded before it would even start fetching the product.
  const restoredProductCodeRef = React.useRef(null);
  useEffect(() => {
    const returnProductCode = location.state?.returnParams?.openProductCode;
    if (!returnProductCode) return;

    restoredProductCodeRef.current = returnProductCode;
    // Show something immediately; the real label/description get filled
    // in below once the product dropdown list is available.
    setSelectedProduct({ value: returnProductCode, label: returnProductCode });
    fetchProductData(returnProductCode);
    if (location.state?.returnParams?.activeTab) {
      setActiveTab(location.state.returnParams.activeTab);
    }
    // Clear the navigation state so this doesn't re-trigger on later renders
    window.history.replaceState({}, document.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Once the product dropdown list has loaded, swap in the proper
  // label/description for the restored product. Purely cosmetic — the
  // actual planning data was already fetched above without waiting for this.
  useEffect(() => {
    const returnProductCode = restoredProductCodeRef.current;
    if (!returnProductCode || products.length === 0) return;
    const match = productOptions.find((opt) => opt.value === returnProductCode);
    if (match) {
      setSelectedProduct(match);
      setProductDescription(match.description || "");
    }
    restoredProductCodeRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  // Product Type is the only top filter bar field whose options never depend
  // on another filter, so it's the only one loaded statically here — same
  // split Pegging.js uses (see fetchProductTypes there).
  const fetchDropdowns = async () => {
    try {
      const [productRes, processRes, productTypeRes] = await Promise.all([
        axios.get(`${ORDER_API}/planning/finished-goods-products`),
        axios.get(`${ORDER_API}/planning/processes`),
        axios.get(`${MASTER_API}/configuration/product-types`),
      ]);

      setDropdownData((prev) => ({ ...prev, productTypes: productTypeRes.data || [] }));
      setProducts(productRes.data);
      setProcessOptions(processRes.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  // Loads Category/Cable Type/Code/Color/Cores/Cross Section/Std. Ref/Voltage/Construction
  // options scoped to whatever's currently selected — mirrors Pegging.js's
  // fetchDependentFilterOptions so picking a Product Type (or any other filter)
  // here narrows these dropdowns to only the values that actually occur for
  // that selection, instead of always showing every value that ever exists.
  const fetchDependentFilterOptions = useCallback(async () => {
    try {
      const res = await axios.get(`${MASTER_API}/product-master/filter-options`, {
        params: {
          productType: filterValues.productType || "",
          category: filterValues.category || "",
          cableType: filterValues.cableType || "",
          code: filterValues.code || "",
          color: filterValues.color || "",
          noOfCores: filterValues.noOfCores || "",
          nominalCrossSection: filterValues.nominalCrossSection || "",
          referenceOfStandard: filterValues.referenceOfStandard || "",
          voltage: filterValues.voltage || "",
          construction: filterValues.construction || "",
          // Planning has no Sub Category / Quality Category filter fields of its
          // own, but the backend endpoint expects these keys to be present
          // (same as Pegging always sends them) to build the right query —
          // omitting them was causing the "Unknown column 'category'" error.
          subCategory: "",
          qualityCategory: "",
        },
      });
      const data = res.data || {};
      // The endpoint returns flat string arrays; reshape to {name} objects so the
      // existing dropdown rendering (which reads `item.name`) needs no other changes.
      const toNameObjs = (arr) => (arr || []).map((v) => ({ name: v }));
      setDropdownData((prev) => ({
        ...prev,
        categories: toNameObjs(data.category),
        cableTypes: toNameObjs(data.cableType),
        codes: toNameObjs(data.code),
        colours: toNameObjs(data.color),
        cores: toNameObjs(data.noOfCores),
        nominalCrossSections: toNameObjs(data.nominalCrossSection),
        referenceOfStandards: toNameObjs(data.referenceOfStandard),
        voltages: toNameObjs(data.voltage),
        constructions: toNameObjs(data.construction),
      }));
    } catch (err) {
      console.error("Failed to load dependent filter options:", err);
    }
  }, [
    filterValues.productType,
    filterValues.category,
    filterValues.cableType,
    filterValues.code,
    filterValues.color,
    filterValues.noOfCores,
    filterValues.nominalCrossSection,
    filterValues.referenceOfStandard,
    filterValues.voltage,
    filterValues.construction,
  ]);

  useEffect(() => {
    fetchDependentFilterOptions();
  }, [fetchDependentFilterOptions]);

  useEffect(() => {
    const activeFilters = Object.keys(filterValues).filter(k => filterValues[k]);

    // If we have active filters, fetch the batch data narrowed by those filters
    if (activeFilters.length > 0) {
      fetchFilteredData(filterValues);
    } else if (selectedProduct) {
      // Fallback to single product if filters are cleared but a product is selected
      fetchProductData(selectedProduct.value);
    } else {
      // No filters and no product selected — show NOTHING. Filters/product
      // selection are required to gate the data; don't auto-load everything.
      setPlanningElements([]);
      setPeggingData([]);
    }
    // fetchFilteredData is redefined every render (not memoized) and
    // selectedProduct is only read as a fallback when filters are cleared —
    // intentionally NOT deps here, since adding fetchFilteredData would
    // re-run this effect on every render, and selectedProduct changes are
    // already handled directly by the product dropdown's own onChange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues]); // Triggers every time a filter is changed

  // Jump back to page 1 whenever the selected product changes, so the
  // Elements table doesn't stay stuck on a page that no longer has data.
  useEffect(() => {
    setElementsPage(1);
  }, [selectedProduct]);

  const fetchFilteredData = async (filters) => {
    try {
      const res = await axios.post(`${ORDER_API}/planning/filtered-demand`, filters);
      // Overwrite the table states with the batched results
      setPlanningElements(res.data.planningElements || []);
      setPeggingData(res.data.peggingData || []);

      // Clear specific product dropdown selection so they don't conflict conceptually
      setSelectedProduct(null);
      setProductDescription("");
    } catch (err) {
      console.error("Failed to fetch filtered data", err);
      showToast("Failed to fetch filtered data.", "error");
    }
  };

  const fetchProductData = async (productCode) => {
    try {
      const [
        elementsRes,
        peggingRes,
      ] = await Promise.all([
        axios.get(`${ORDER_API}/planning/exploded-demand/${productCode}`),
        axios.get(`${ORDER_API}/planning/pegging/${productCode}`),
      ]);
      setPlanningElements(elementsRes.data);
      setPeggingData(peggingRes.data);
    } catch (err) {
      console.log(err);
    }
  };

  const handleOpenPlCharacteristics = (characteristics) => {
    const modalData = (characteristics || []).map((char) => ({
      characteristic: char.characteristic,
      value: char.value || "",
    }));
    setSelectedCharacteristics(modalData);
    setModalVisible(true);
  };

  const handleRunMRP = async () => {
    // Process-driven bulk run: a Process is selected and no single product
    // is selected — run MRP for every product code linked to that process
    // (bom.process_id), same explosion logic, just looped server-side.
    if (!selectedProduct && filterValues.processId) {
      setMrpRunning(true);
      try {
        const res = await axios.post(`${ORDER_API}/planning/run-mrp-process`, {
          processId: filterValues.processId,
          userId: getCurrentUser(),
        });

        const results = res.data.results || [];
        const succeeded = results.filter((r) => r.success).length;
        const failed = results.length - succeeded;

        showToast(
          `MRP Run completed for process ${filterValues.processId}. ` +
          `Products processed: ${results.length} (Success: ${succeeded}, Failed: ${failed}). ` +
          (res.data.warning ? `\n\n⚠️ ${res.data.warning}` : ""),
          "success",
          6000
        );
      } catch (err) {
        console.error(err);
        showToast("MRP Run failed.", "error");
      } finally {
        setMrpRunning(false);
      }
      return;
    }

    if (!selectedProduct) {
      showToast("Please select a product first.", "error");
      return;
    }

    setMrpRunning(true);
    try {
      const res = await axios.post(`${ORDER_API}/planning/run-mrp`, {
        productCode: selectedProduct.value,
        userId: getCurrentUser(),
      });

      //eslint-disable-next-line
      const totalQty = res.data?.totalQty ?? res.data?.totalDemand ?? 0;
      showToast(
        `MRP Run completed Successfully.` + (res.data.warning ? `\n\n⚠️ ${res.data.warning}` : ""),
        "success",
        6000
      );
      await fetchProductData(selectedProduct.value);
    } catch (err) {
      console.error(err);
      showToast("MRP Run failed.", "error");
    } finally {
      setMrpRunning(false);
    }
  };

  // Calls the same /pegging/run endpoint the standalone Pegging page uses —
  // no backend changes here, just triggering it for the currently selected
  // product from Product Planning too.
  const handleRunPegging = async () => {
    // Matches Pegging.js: no requirement to select anything — running with
    // nothing selected triggers an unscoped pegging run for everything;
    // selecting a product or filters scopes the run to that subset.
    // Returns true/false so callers (like the Schedule modal's own Pegging
    // button) know whether it's safe to close the modal afterward.
    setPeggingRunning(true);
    try {
      await axios.post(`${ORDER_API}/pegging/run`, {
        productCode: selectedProduct?.value || null,
        ...filterValues,
      });
      showToast("Pegging Run completed Successfully.", "success");
      if (selectedProduct?.value) {
        await fetchProductData(selectedProduct.value);
      } else {
        await fetchFilteredData(filterValues);
      }
      return true;
    } catch (err) {
      console.error(err);
      showToast("Pegging Run failed.", "error");
      return false;
    } finally {
      setPeggingRunning(false);
    }
  };

  // Auto MRP — explodes the BOM top-down all the way to raw materials,
  // instead of only planning one level. Reuses the existing per-product
  // Only "Immediate" calls /planning/run-mrp-auto directly (runs now).
  // Hourly/Daily/Weekly/Monthly persist a row via /planning/mrp-schedule —
  // the server registers a real node-cron job for it (see
  // planning_auto_mrp_schedule.service.js) that fires runMRPAutoExploded /
  // runMRPAutoAll on its own from then on, including after a server restart.
  const handleSaveSchedule = async () => {
    if (autoMrpScope === "product" && !autoMrpProduct) {
      showToast("Please select a product code, or choose 'All Finished Goods'.", "error");
      return;
    }
    if (autoMrpScope === "process" && !autoMrpProcess) {
      showToast("Please select a process.", "error");
      return;
    }

    if (autoMrpFrequency !== "immediate") {
      setAutoMrpRunning(true);
      try {
        await axios.post(`${ORDER_API}/planning/mrp-schedule`, {
          scope: autoMrpScope,
          productCode: autoMrpScope === "product" ? autoMrpProduct.value : undefined,
          processId: autoMrpScope === "process" ? autoMrpProcess.value : undefined,
          frequency: autoMrpFrequency,
          minuteOfHour: autoMrpFrequency === "hourly" ? (autoMrpHour.split(":")[1] || "00") : undefined,
          timeOfDay: autoMrpFrequency !== "hourly" ? autoMrpHour : undefined,
          dayOfWeek: autoMrpFrequency === "weekly" ? autoMrpDayOfWeek : undefined,
          dayOfMonth: autoMrpFrequency === "monthly" ? autoMrpDayOfMonth : undefined,
          userId: getCurrentUser(),
        });
        await loadMrpSchedules();
        const scopeLabel =
          autoMrpScope === "product" ? autoMrpProduct.value :
            autoMrpScope === "process" ? autoMrpProcess.label :
              autoMrpScope === "allProcess" ? "All Processes" :
                "All Finished Goods";
        showToast(
          `Auto MRP schedule saved (${autoMrpFrequency}) for ` +
          `${scopeLabel}. ` +
          `It will now run on its own.`,
          "success"
        );
      } catch (err) {
        console.error(err);
        showToast("Saving the schedule failed: " + (err.response?.data?.error || err.message), "error");
      } finally {
        setAutoMrpRunning(false);
      }
      return;
    }

    setAutoMrpRunning(true);
    try {
      if (autoMrpScope === "process") {
        const res = await axios.post(`${ORDER_API}/planning/run-mrp-process`, {
          processId: autoMrpProcess.value,
          userId: getCurrentUser(),
        });
        const results = res.data?.results || [];
        const succeeded = results.filter((r) => r.success).length;
        showToast(
          `MRP run completed for process ${autoMrpProcess.label}. ` +
          `Products processed: ${results.length} (Success: ${succeeded}, Failed: ${results.length - succeeded}). ` +
          (res.data.warning ? `\n\n⚠️ ${res.data.warning}` : ""),
          "success",
          6000
        );
        // Don't close the modal here — the new MRP demand/supply hasn't been
        // pegged yet. Leave the modal open so the user clicks "PEGGING"
        // (below) themselves; the modal only closes once that succeeds.
        if (selectedProduct) await fetchProductData(selectedProduct.value);
        return;
      }

      if (autoMrpScope === "allProcess") {
        const res = await axios.post(`${ORDER_API}/planning/run-mrp-all-process`, { userId: getCurrentUser() });
        const results = res.data?.results || [];
        const succeeded = results.filter((r) => r.success).length;
        showToast(
          `MRP run completed for ${res.data?.totalProcesses || 0} process(es). ` +
          `Products processed: ${results.length} (Success: ${succeeded}, Failed: ${results.length - succeeded}). ` +
          (res.data.warning ? `\n\n⚠️ ${res.data.warning}` : ""),
          "success",
          6000
        );
        if (selectedProduct) await fetchProductData(selectedProduct.value);
        return;
      }

      const res = await axios.post(`${ORDER_API}/planning/run-mrp-auto`, {
        productCode: autoMrpScope === "product" ? autoMrpProduct.value : undefined,
        userId: getCurrentUser(),
      });

      if (autoMrpScope === "product") {
        const results = res.data?.results || [];
        const succeeded = results.filter((r) => r.success).length;
        showToast(
          `Auto MRP completed for ${res.data?.productCode || autoMrpProduct.value} ` +
          `(exploded ${results.length} product code(s), ${succeeded} succeeded). ` +
          (res.data.warning ? `\n\n⚠️ ${res.data.warning}` : ""),
          "success",
          6000
        );
      } else {
        const results = res.data?.results || [];
        const succeeded = results.filter((r) => r.success).length;
        showToast(
          `Auto MRP completed for ${res.data?.totalFinishedGoods || 0} finished good(s) ` +
          `(${results.length} product code(s) exploded, ${succeeded} succeeded). ` +
          (res.data.warning ? `\n\n⚠️ ${res.data.warning}` : ""),
          "success",
          6000
        );
      }
      // Same here — stay open until the user runs Pegging themselves.
      if (selectedProduct) await fetchProductData(selectedProduct.value);
    } catch (err) {
      console.error(err);
      showToast("Auto MRP failed.", "error");
    } finally {
      setAutoMrpRunning(false);
    }
  };

  // Loads persisted schedules so the modal can show what's already active
  // and let the user delete one.
  const loadMrpSchedules = async () => {
    try {
      const res = await axios.get(`${ORDER_API}/planning/mrp-schedule`);
      setMrpSchedules(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteSchedule = async (id) => {
    try {
      await axios.delete(`${ORDER_API}/planning/mrp-schedule/${id}`);
      await loadMrpSchedules();
      showToast("Schedule deleted.", "success");
    } catch (err) {
      console.error(err);
      showToast("Deleting the schedule failed.", "error");
    }
  };

  // Track which supplies have been rendered so we don't duplicate them.
  // FIX: planning_pegging_lock keeps its own audit-copy row for every
  // Production Order / Purchase Order / Stock item that sections 1-3 already
  // pull live from their own tables (production_orders, purchase_orders,
  // stock). Previously this Set only got populated while walking pegged
  // supply under ROP/SO/Planned-Order rows (sections 4-6), so the SAME
  // production order / stock batch shown live in section 1-3 would show up
  // AGAIN from planning_pegging_lock in section 7's catch-all. Seed it here
  // with every identifier already rendered live, before section 7 runs.
  const renderedSupplies = new Set([
    ...productionOrders.map((p) => p.production_order_id).filter(Boolean),
    ...purchaseOrders.map((p) => p.purchase_order_id).filter(Boolean),
    ...stockData.map((s) => s.batch_number).filter(Boolean),
  ]);

  // Same idea for Reorder Point: the "Reorder Point" row (section 4) reads the
  // master config value from productdetails.reorderpoint, while a "ROP"
  // category row in planningElements is planning_pegging_lock's own locked
  // copy of that same demand. If pegging_lock already has one, don't also
  // show the master-config row — they're the same requirement shown twice.
  const ropAlreadyPegged = planningElements.some((el) => el.category === "ROP");

  // Helper to render Planned Order / Purchase Request Rows dynamically
  const renderPlannedOrderRow = (item, keySuffix) => {
    return (
      <tr key={`pl-${item.pl_number}-${keySuffix}`}>
        <td style={tableCell}>{item.product_code || selectedProduct?.value || "-"}</td>
        <td style={tableCell}>
          {item.requirement_date
            ? new Date(item.requirement_date).toLocaleDateString("en-IN")
            : "-"}
        </td>
        <td style={tableCell}>
          {item.availability_date
            ? new Date(item.availability_date).toLocaleDateString("en-IN")
            : "-"}
        </td>
        <td style={tableCell}>{item.category}</td>
        <td style={tableCell}>
          {item.category === "Planned Order" ? (
            <span
              onClick={() => {
                navigate("/planned-order", {
                  state: {
                    openPlannedOrderNo: item.pl_number,
                    returnTo: location.pathname,
                    returnParams: { openProductCode: selectedProduct?.value, activeTab: activeTab }
                  }
                });
              }}
              style={{ color: "#1976d2", cursor: "pointer", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: "2px" }}
            >
              {item.pl_number}
            </span>
          ) : item.category === "Purchase Request" ? (
            <span
              onClick={() => {
                navigate("/cable/procurement/purchase-request", {
                  state: {
                    openPrNo: item.pl_number,
                    returnTo: location.pathname,
                    returnParams: { openProductCode: selectedProduct?.value, activeTab: activeTab }
                  }
                });
              }}
              style={{ color: "#1976d2", cursor: "pointer", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: "2px" }}
            >
              {item.pl_number}
            </span>
          ) : item.category === "Production Order" ? (
            <span
              onClick={() => {
                navigate("/cable/production-order", {
                  state: {
                    openProductionOrderNo: item.pl_number,
                    returnTo: location.pathname,
                    returnParams: { openProductCode: selectedProduct?.value, activeTab: activeTab }
                  }
                });
              }}
              style={{ color: "#1976d2", cursor: "pointer", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: "2px" }}
            >
              {item.pl_number}
            </span>
          ) : item.category === "Sales Order" ? (
            <span
              onClick={() => {
                navigate("/cable/sales/sales-order", {
                  state: {
                    openSoId: item.pl_number,
                    returnTo: location.pathname,
                    returnParams: { openProductCode: selectedProduct?.value, activeTab: activeTab }
                  }
                });
              }}
              style={{ color: "#1976d2", cursor: "pointer", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: "2px" }}
            >
              {item.pl_number}
            </span>
          ) : (
            item.pl_number
          )}
        </td>
        {/* NOTE: qty coming from getExplodedDemandByProduct is always Math.abs()'d,
            so we can no longer key color off the sign of item.qty (that's why
            Sales Order / ROP req rows were showing green/+ instead of red/-).
            item.side ("demand" | "supply") tells us which it actually is. */}
        <td style={{ ...tableCell, color: item.side === "demand" ? "red" : "green", fontWeight: 700 }}>
          {item.side === "demand" ? "-" : "+"}{Number(item.qty).toLocaleString("en-IN")}
        </td>
        <td style={tableCell}>{item.uom || "-"}</td>
        <td style={tableCell}>
          {item.pl_number && reorderData?.productClass && (
            <span
              style={{ cursor: "pointer", color: "#050505", fontWeight: "bold" }}
              onClick={() => handleOpenPlCharacteristics(item.characteristics)}
              title="View Characteristics"
            >
              ▶
            </span>
          )}
        </td>
      </tr>
    );
  };

  const handleFilterChange = (key, selected) => {
    setFilterValues((prev) => ({ ...prev, [key]: selected?.value || "" }));
  };

  return (
    <Layout>
      <div style={{ padding: 20 }}>

        {/* TOAST MESSAGE */}
        {toast && (
          <div
            style={{
              position: "fixed",
              top: 20,
              right: 20,
              zIndex: 9999,
              minWidth: 260,
              maxWidth: 420,
              padding: "12px 16px",
              borderRadius: 6,
              color: "#fff",
              fontSize: 13,
              fontWeight: 500,
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              background: toast.type === "error" ? "#d32f2f" : "#2e7d32",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              animation: "toastSlideIn 0.25s ease-out"
            }}
          >
            <span style={{ whiteSpace: "pre-line" }}>{toast.message}</span>
            <button
              onClick={() => setToast(null)}
              style={{
                background: "transparent",
                border: "none",
                color: "#fff",
                cursor: "pointer",
                fontSize: 16,
                lineHeight: 1,
                padding: 0
              }}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        )}
        <style>{`
          @keyframes toastSlideIn {
            from { transform: translateX(30px); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
        `}</style>

        {/* HEADER */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16
          }}
        >
          <h2 style={pageTitle}>Product Planning</h2>

          <div style={{ display: "flex", gap: "10px" }}>
            <button
              style={{ ...runButtonStyle, background: "#0080ff" }}
              onClick={() => {
                setScheduleModal(true);
                loadMrpSchedules();
              }}
            >
              ⏱️ Schedule run MRP
            </button>

            <button
              style={{
                ...runButtonStyle,
                opacity: mrpRunning ? 0.7 : 1,
                cursor: mrpRunning ? "not-allowed" : "pointer"
              }}
              onClick={handleRunMRP}
              disabled={mrpRunning}
            >
              {mrpRunning ? "RUNNING..." : "RUN MRP"}
            </button>

            <button
              style={{
                ...runButtonStyle,
                opacity: peggingRunning ? 0.7 : 1,
                cursor: peggingRunning ? "not-allowed" : "pointer"
              }}
              onClick={handleRunPegging}
              disabled={peggingRunning}
            >
              {peggingRunning ? "RUNNING..." : "PEGGING"}
            </button>
            <button
              onClick={() => {
                setManualExists(null);
                setManualFile(null);
                setShowManual(true);
                fetch(USER_MANUAL_LIST_API, { headers: authHeaders() })
                  .then((res) => res.json())
                  .then((data) => {
                    const norm = (v) => String(v || "").trim().toLowerCase();
                    const match = (data?.data || []).find((m) =>
                      norm(m.module_name) === norm(USER_MANUAL_SCOPE.moduleName) &&
                      norm(m.process_name) === norm(USER_MANUAL_SCOPE.process) &&
                      norm(m.sub_process_name) === norm(USER_MANUAL_SCOPE.subProcess)
                    );
                    if (match && match.drive_link) {
                      setManualFile(match);
                      setManualExists(true);
                    } else {
                      setManualExists(false);
                    }
                  })
                  .catch(() => setManualExists(false));
              }}
              title="View User Manual"
              style={{
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                padding: "10px 14px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
                color: "#3b82f6",
                fontSize: "14px",
                fontWeight: "600",
                boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#3b82f6"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; }}
            >
              <FileText size={18} />
              User Manual
            </button>
          </div>
        </div>

        {/* --- User Manual Popup Modal --- */}
        {showManual && (
          <div
            onClick={() => setShowManual(false)}
            style={{
              position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
              backgroundColor: "rgba(15, 23, 42, 0.6)", display: "flex",
              alignItems: "center", justifyContent: "center", zIndex: 1100,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "#ffffff",
                borderRadius: "12px",
                width: "90vw",
                height: "97vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)",
              }}
            >
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "14px 20px", borderBottom: "1px solid #e2e8f0",
              }}>
                <h4 style={{ margin: 0, color: "#0f172a", fontSize: "16px", fontWeight: "600" }}>
                  User Manual
                </h4>
                <button
                  onClick={() => setShowManual(false)}
                  style={{
                    border: "none", background: "#f1f5f9", borderRadius: "8px",
                    width: "32px", height: "32px", cursor: "pointer", fontSize: "18px",
                    color: "#475569", lineHeight: 1,
                  }}
                  title="Close"
                >
                  ×
                </button>
              </div>

              {manualExists === null && (
                <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: "14px" }}>
                  Checking for user manual...
                </div>
              )}

              {manualExists === false && (
                <div style={{
                  flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
                  justifyContent: "center", color: "#64748b", gap: "10px", padding: "20px", textAlign: "center"
                }}>
                  <FileText size={40} color="#cbd5e1" />
                  <div style={{ fontSize: "15px", fontWeight: 600, color: "#334155" }}>
                    User Manual Not Available
                  </div>
                  <div style={{ fontSize: "13px", color: "#94a3b8", maxWidth: "320px" }}>
                    The user manual for this module hasn't been uploaded yet. Please check back later or contact your administrator.
                  </div>
                </div>
              )}

              {manualExists === true && manualFile && (() => {
                const MANUAL_SCALE = 1.25;
                return (
                  <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
                    <iframe
                      src={manualFile.drive_link.replace("/view", "/preview")}
                      title={manualFile.file_name || "User Manual"}
                      allow="autoplay"
                      style={{
                        border: "none",
                        width: `${100 / MANUAL_SCALE}%`,
                        height: `${100 / MANUAL_SCALE}%`,
                        transform: `scale(${MANUAL_SCALE})`,
                        transformOrigin: "top left",
                      }}
                    />
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        <div style={filterCard}>
          {/* Accordion header — click to show / hide the filters */}
          <button
            type="button"
            onClick={() => setFiltersOpen((o) => !o)}
            aria-expanded={filtersOpen}
            aria-controls="filters-body"
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0 0 10px 0",
              background: "transparent",
              border: "none",
              borderBottom: filtersOpen ? "1px solid #eef0f3" : "none",
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {activeFilterCount > 0 && (
                <span
                  style={{
                    background: "#0d6efd",
                    color: "#fff",
                    borderRadius: 10,
                    padding: "1px 8px",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {activeFilterCount} active
                </span>
              )}
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 14 }}>
              {activeFilterCount > 0 && (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    clearAllFilters();
                  }}
                  style={{
                    padding: "4px 10px",
                    background: "#fff",
                    color: "#c0392b",
                    border: "1px solid #c0392b",
                    borderRadius: 4,
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  Clear Filters
                </span>
              )}
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "5px 12px",
                  background: "#fff",
                  color: "#334155",
                  border: "1px solid #dee2e6",
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {filtersOpen ? "Hide Filters" : "Show Filters"}
                <span
                  style={{
                    display: "inline-block",
                    fontSize: 12,
                    color: "#555",
                    transition: "transform 0.25s ease",
                    transform: filtersOpen ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                >
                  ▼
                </span>
              </span>
            </span>
          </button>

          <div
            id="filters-body"
            style={{
              display: "grid",
              gridTemplateRows: filtersOpen ? "1fr" : "0fr",
              transition: "grid-template-rows 0.3s ease",
            }}
          >
            <div style={{ overflow: filtersOpen ? "visible" : "hidden", minHeight: 0 }}>
              <div style={{ paddingTop: 14 }}>
                {/* FILTERS - Product Type (matches Pegging.js top row) */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(200px, 260px) minmax(200px, 260px)",
                    gap: "14px",
                    marginBottom: "14px",
                    alignItems: "end",
                  }}
                >
                  {FILTER_FIELDS.filter((f) => f.key === "productType").map((f) => (
                    <div key={f.key}>
                      <label style={filterLabel}>{f.label}</label>
                      <Select
                        options={(dropdownData[f.dataKey] || []).map((item) => ({
                          value: item.name,
                          label: item.name,
                        }))}
                        value={
                          filterValues[f.key]
                            ? { value: filterValues[f.key], label: filterValues[f.key] }
                            : null
                        }
                        onChange={(selected) => handleFilterChange(f.key, selected)}
                        placeholder="Select"
                        isClearable
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                    </div>
                  ))}

                  <div>
                    <label style={filterLabel}>Process</label>
                    <Select
                      options={processOptions.map((p) => ({
                        value: p.process_id,
                        label: `${p.process_id} - ${p.process_name}`,
                      }))}
                      value={
                        filterValues.processId
                          ? {
                            value: filterValues.processId,
                            label:
                              processOptions.find((p) => p.process_id === filterValues.processId)
                                ? `${filterValues.processId} - ${processOptions.find((p) => p.process_id === filterValues.processId).process_name}`
                                : filterValues.processId,
                          }
                          : null
                      }
                      onChange={(selected) => handleFilterChange("processId", selected)}
                      placeholder="Select Process"
                      isClearable
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                </div>

                {/* Remaining filters in compact box (matches Pegging.js) */}
                <div style={compactFilterBox}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
                    {["category", "cableType", "code", "color", "noOfCores", "nominalCrossSection", "referenceOfStandard", "voltage", "construction"]
                      .map((key) => FILTER_FIELDS.find((f) => f.key === key))
                      .map((f) => (
                        <div
                          key={f.key}
                          style={{
                            minWidth: 120,
                            flex: "1 1 110px",
                            maxWidth: 180,
                            display: "flex",
                            flexDirection: "column",
                            gap: 3,
                          }}
                        >
                          <span style={compactFilterItemLabel}>{f.label}</span>
                          <Select
                            options={(dropdownData[f.dataKey] || []).map((item) => ({
                              value: item.name,
                              label: item.name,
                            }))}
                            value={
                              filterValues[f.key]
                                ? { value: filterValues[f.key], label: filterValues[f.key] }
                                : null
                            }
                            onChange={(selected) => handleFilterChange(f.key, selected)}
                            placeholder="Select"
                            isClearable
                            styles={compactSelectStyles}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                          />
                        </div>
                      ))}
                  </div>
                </div>

                {/* PRODUCT CODE + DESCRIPTION */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "14px"
                  }}
                >
                  <div>
                    <label style={filterLabel}>Product Code</label>
                    <Select
                      options={productOptions}
                      value={selectedProduct}
                      onChange={async (selected) => {
                        setSelectedProduct(selected);
                        setProductDescription(selected?.description || "");

                        setPurchaseOrders([]);
                        setStockData([]);
                        setReorderData(null);
                        setPlanningElements([]);
                        setPeggingData([]);

                        if (selected?.value) {
                          await fetchProductData(selected.value);
                        }
                      }}
                      placeholder="Search Product Code"
                      isClearable
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>

                  <div>
                    <label style={filterLabel}>Product Description</label>
                    <input
                      type="text"
                      value={productDescription}
                      readOnly
                      placeholder="Product Description"
                      style={{
                        width: "100%",
                        height: "32px",
                        border: "1px solid #dee2e6",
                        borderRadius: "4px",
                        padding: "0 10px",
                        fontSize: "13px",
                        background: "#f9f9f9"
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* TABS */}
        <div style={{ display: "flex", gap: 10 }}>
          <button style={tabButton(activeTab === "elements")} onClick={() => setActiveTab("elements")}>
            Elements
          </button>
        </div>

        <hr />

        {/* ===================== ELEMENTS TAB ===================== */}
        {activeTab === "elements" && (() => {
          // Build every row up front (same logic as before, just collected
          // into a plain array instead of being written straight into the
          // <tbody>) so we can paginate over the finished list below.
          const allElementRows = [];

          // 1. PRODUCTION ORDERS
          productionOrders.forEach((item, index) => {
            allElementRows.push(
              <tr key={`prod-${index}`}>
                <td style={tableCell}>{item.product_code || selectedProduct?.value || "-"}</td>
                <td style={tableCell}>
                  {item.requirement_date ? new Date(item.requirement_date).toLocaleDateString("en-IN") : "-"}
                </td>
                <td style={tableCell}>-</td>
                <td style={tableCell}>Production Order</td>
                <td style={tableCell}>
                  {item.production_order_id ? (
                    <span
                      onClick={() => {
                        navigate("/planned-order", {
                          state: {
                            openProductionOrderNo: item.production_order_id,
                            returnTo: location.pathname,
                            returnParams: { openProductCode: selectedProduct?.value, activeTab: activeTab }
                          }
                        });
                      }}
                      style={{ color: "#1976d2", cursor: "pointer", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: "2px" }}
                    >
                      {item.production_order_id}
                    </span>
                  ) : (
                    item.production_order_id
                  )}
                </td>
                <td style={{ ...tableCell, color: "green", fontWeight: 700 }}>
                  +{item.quantity}
                </td>
                <td style={tableCell}>{item.uom || "-"}</td>
                <td style={tableCell}></td>
              </tr>
            );
          });

          // 2. PURCHASE ORDERS
          purchaseOrders.forEach((item, index) => {
            allElementRows.push(
              <tr key={`po-${index}`}>
                <td style={tableCell}>{item.product_code || selectedProduct?.value || "-"}</td>
                <td style={tableCell}>
                  {item.requirement_date ? new Date(item.requirement_date).toLocaleDateString("en-IN") : "-"}
                </td>
                <td style={tableCell}>-</td>
                <td style={tableCell}>Purchase Order</td>
                <td style={tableCell}>{item.purchase_order_id}</td>
                <td style={{ ...tableCell, color: "green", fontWeight: 700 }}>
                  +{item.requirement_qty}
                </td>
                <td style={tableCell}>{item.uom || "-"}</td>
                <td style={tableCell}></td>
              </tr>
            );
          });

          // 3. STOCK
          stockData.forEach((item, index) => {
            allElementRows.push(
              <tr key={`stock-${index}`}>
                <td style={tableCell}>{item.product_code || selectedProduct?.value || "-"}</td>
                <td style={tableCell}>-</td>
                <td style={tableCell}>-</td>
                <td style={tableCell}>Stock</td>
                <td style={tableCell}>{item.batch_number}</td>
                <td style={{ ...tableCell, color: "green", fontWeight: 700 }}>
                  +{item.qty}
                </td>
                <td style={tableCell}>{item.uom || "-"}</td>
                <td style={tableCell}></td>
              </tr>
            );
          });

          // 4. REORDER POINT -> SUPPLIES
          if (reorderData?.reorderpoint && !ropAlreadyPegged) {
            allElementRows.push(
              <tr key="rop-demand">
                <td style={tableCell}>{reorderData.productCode || selectedProduct?.value || "-"}</td>
                <td style={tableCell}></td>
                <td style={tableCell}></td>
                <td style={tableCell}>Reorder Point</td>
                <td style={tableCell}></td>
                <td style={{ ...tableCell, color: "red", fontWeight: 700 }}>
                  -{reorderData.reorderpoint}
                </td>
                <td style={tableCell}>{reorderData.reorderpoint_uom}</td>
                <td style={tableCell}></td>
              </tr>
            );
            const ropPegs = peggingData.filter(p => p.parent_category === 'Reorder Point');
            ropPegs.forEach((peg, idx) => {
              const supplyEl = planningElements.find(el => el.pl_number === peg.child_element_description);
              if (supplyEl && !renderedSupplies.has(supplyEl.pl_number)) {
                renderedSupplies.add(supplyEl.pl_number);
                allElementRows.push(renderPlannedOrderRow(supplyEl, `rop-sup-${idx}`, 1));
              }
            });
          }

          // 6. PARENT DEMAND ROWS -> SUPPLIES
          (() => {
            // Find all unique parent PLs/PRs that demand this component
            const parentPegs = peggingData.filter(p => p.parent_category === 'Planned Order' || p.parent_category === 'Purchase Request');

            const parentGroups = {};
            parentPegs.forEach(peg => {
              if (!parentGroups[peg.parent_element_description]) {
                parentGroups[peg.parent_element_description] = [];
              }
              parentGroups[peg.parent_element_description].push(peg);
            });

            return Object.keys(parentGroups).flatMap((parentPlNumber, idx) => {
              const pegs = parentGroups[parentPlNumber];
              const firstPeg = pegs[0];

              const parentReqDate = firstPeg.parent_req_date;
              const parentAvailDate = firstPeg.parent_avail_date;
              const parentQty = firstPeg.parent_qty;

              const parentDemandRow = (
                <tr key={`parent-dem-${idx}`}>
                  <td style={tableCell}>{firstPeg.product_code || selectedProduct?.value || "-"}</td>
                  <td style={tableCell}>
                    {parentReqDate ? new Date(parentReqDate).toLocaleDateString("en-IN") : "-"}
                  </td>
                  <td style={tableCell}>
                    {parentAvailDate ? new Date(parentAvailDate).toLocaleDateString("en-IN") : "-"}
                  </td>
                  <td style={tableCell}>{firstPeg.parent_category}</td>
                  <td style={tableCell}>
                    <span
                      onClick={() => {
                        if (firstPeg.parent_category === 'Planned Order') {
                          navigate("/planned-order", {
                            state: { openPlannedOrderNo: parentPlNumber, returnTo: location.pathname, returnParams: { openProductCode: selectedProduct?.value, activeTab: activeTab } }
                          });
                        } else if (firstPeg.parent_category === 'Purchase Request') {
                          navigate("/cable/procurement/purchase-request", {
                            state: { openPrNo: parentPlNumber, returnTo: location.pathname, returnParams: { openProductCode: selectedProduct?.value, activeTab: activeTab } }
                          });
                        }
                      }}
                      style={{ color: "#1976d2", cursor: "pointer", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: "2px" }}
                    >
                      {parentPlNumber}
                    </span>
                  </td>
                  <td style={{ ...tableCell, color: "red", fontWeight: 700 }}>
                    -{Number(parentQty).toLocaleString("en-IN")}
                  </td>
                  <td style={tableCell}>{firstPeg.parent_uom || "-"}</td>
                  <td style={tableCell}></td>
                </tr>
              );

              const supplyRows = pegs.map((peg, pegIndex) => {
                const supplyEl = planningElements.find(el => el.pl_number === peg.child_element_description);
                if (supplyEl && !renderedSupplies.has(supplyEl.pl_number)) {
                  renderedSupplies.add(supplyEl.pl_number);
                  return renderPlannedOrderRow(supplyEl, `par-sup-${idx}-${pegIndex}`, 1); // Depth = 1 for indent
                }
                return null;
              });

              allElementRows.push(parentDemandRow, ...supplyRows.filter(Boolean));
            });
          })();

          // 7. REMAINING SUPPLIES (Catch-all for any un-pegged items)
          planningElements
            .filter(el => !renderedSupplies.has(el.pl_number))
            .forEach((item, index) => allElementRows.push(renderPlannedOrderRow(item, `rem-${index}`, 0)));

          const totalElementRows = allElementRows.length;
          const totalElementPages = Math.max(1, Math.ceil(totalElementRows / elementsPageSize));
          const safeElementsPage = Math.min(elementsPage, totalElementPages);
          const pageStart = (safeElementsPage - 1) * elementsPageSize;
          const pagedElementRows = allElementRows.slice(pageStart, pageStart + elementsPageSize);

          return (
            <>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  marginTop: 10,
                  tableLayout: "fixed"
                }}
              >
                <thead>
                  <tr>
                    <th style={{ ...tableHeader, width: "15%" }}>Product Code</th>
                    <th style={{ ...tableHeader, width: "15%" }}>Req Date</th>
                    <th style={{ ...tableHeader, width: "15%" }}>Avail Date</th>
                    <th style={{ ...tableHeader, width: "15%" }}>Category</th>
                    <th style={{ ...tableHeader, width: "18%" }}>Element Description</th>
                    <th style={{ ...tableHeader, width: "10%" }}>Req / Rec Qty</th>
                    <th style={{ ...tableHeader, width: "7%" }}>UOM</th>
                    <th style={{ ...tableHeader, width: "5%" }}></th>
                  </tr>
                </thead>

                <tbody>
                  {pagedElementRows}

                  {/* EMPTY STATE */}
                  {totalElementRows === 0 && (
                    <tr>
                      <td colSpan="8" style={tableCell}>
                        Select a product to view data.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* PAGINATION CONTROLS */}
              {totalElementRows > 0 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginTop: 10,
                    fontSize: "13px",
                    color: "#555"
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span>Rows per page:</span>
                    <select
                      value={elementsPageSize}
                      onChange={(e) => {
                        setElementsPageSize(Number(e.target.value));
                        setElementsPage(1);
                      }}
                      style={{
                        padding: "3px 6px",
                        borderRadius: 4,
                        border: "1px solid #ccc",
                        fontSize: "13px"
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span>
                      {pageStart + 1}–{Math.min(pageStart + elementsPageSize, totalElementRows)} of {totalElementRows}
                    </span>
                    <button
                      onClick={() => setElementsPage((p) => Math.max(1, p - 1))}
                      disabled={safeElementsPage <= 1}
                      style={{
                        padding: "4px 10px",
                        borderRadius: 4,
                        border: "1px solid #ccc",
                        background: safeElementsPage <= 1 ? "#f2f2f2" : "#fff",
                        cursor: safeElementsPage <= 1 ? "not-allowed" : "pointer"
                      }}
                    >
                      Prev
                    </button>
                    <span>
                      Page {safeElementsPage} of {totalElementPages}
                    </span>
                    <button
                      onClick={() => setElementsPage((p) => Math.min(totalElementPages, p + 1))}
                      disabled={safeElementsPage >= totalElementPages}
                      style={{
                        padding: "4px 10px",
                        borderRadius: 4,
                        border: "1px solid #ccc",
                        background: safeElementsPage >= totalElementPages ? "#f2f2f2" : "#fff",
                        cursor: safeElementsPage >= totalElementPages ? "not-allowed" : "pointer"
                      }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          );
        })()}


        {/* SCHEDULE MODAL */}
        {scheduleModal && (
          <div
            style={{
              position: "fixed",
              top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000
            }}
          >
            <div style={{ background: "#fff", padding: "20px", borderRadius: "8px", width: "440px" }}>
              <h3 style={{ marginTop: 0 }}>Schedule run MRP</h3>

              <div style={{ marginBottom: "15px" }}>
                <label style={{ display: "block", fontWeight: "bold", marginBottom: "5px" }}>
                  Frequency:
                </label>
                <select
                  style={{ width: "100%", padding: "8px" }}
                  value={autoMrpFrequency}
                  onChange={(e) => setAutoMrpFrequency(e.target.value)}
                >
                  <option value="immediate">Immediate (run now)</option>
                  <option value="hourly">Hourly</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              {autoMrpFrequency === "hourly" && (
                <div style={{ marginBottom: "15px" }}>
                  <label style={{ display: "block", fontWeight: "bold", marginBottom: "5px" }}>
                    Minute of every hour:
                  </label>
                  <select
                    style={{ width: "100%", padding: "8px" }}
                    value={autoMrpHour.split(":")[1] || "00"}
                    onChange={(e) => setAutoMrpHour(`00:${e.target.value}`)}
                  >
                    {["00", "15", "30", "45"].map((m) => (
                      <option key={m} value={m}>:{m}</option>
                    ))}
                  </select>
                </div>
              )}

              {(autoMrpFrequency === "daily" || autoMrpFrequency === "weekly" || autoMrpFrequency === "monthly") && (
                <div style={{ marginBottom: "15px" }}>
                  <label style={{ display: "block", fontWeight: "bold", marginBottom: "5px" }}>
                    Time of day:
                  </label>
                  <input
                    type="time"
                    style={{ width: "100%", padding: "8px" }}
                    value={autoMrpHour}
                    onChange={(e) => setAutoMrpHour(e.target.value)}
                  />
                </div>
              )}

              {autoMrpFrequency === "weekly" && (
                <div style={{ marginBottom: "15px" }}>
                  <label style={{ display: "block", fontWeight: "bold", marginBottom: "5px" }}>
                    Day of week:
                  </label>
                  <select
                    style={{ width: "100%", padding: "8px" }}
                    value={autoMrpDayOfWeek}
                    onChange={(e) => setAutoMrpDayOfWeek(Number(e.target.value))}
                  >
                    {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map((d, i) => (
                      <option key={d} value={i}>{d}</option>
                    ))}
                  </select>
                </div>
              )}

              {autoMrpFrequency === "monthly" && (
                <div style={{ marginBottom: "15px" }}>
                  <label style={{ display: "block", fontWeight: "bold", marginBottom: "5px" }}>
                    Day of month:
                  </label>
                  <select
                    style={{ width: "100%", padding: "8px" }}
                    value={autoMrpDayOfMonth}
                    onChange={(e) => setAutoMrpDayOfMonth(Number(e.target.value))}
                  >
                    {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                  <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
                    Capped at 28 so it lands on every month, including February.
                  </div>
                </div>
              )}

              <div style={{ marginBottom: "15px" }}>
                <label style={{ display: "block", fontWeight: "bold", marginBottom: "5px" }}>
                  Run For:
                </label>
                <select
                  style={{ width: "100%", padding: "8px", marginBottom: "8px" }}
                  value={autoMrpScope}
                  onChange={(e) => setAutoMrpScope(e.target.value)}
                >
                  <option value="all">All Finished Goods (FG → RM)</option>
                  <option value="product">Product Code</option>
                  <option value="process">Process</option>
                  <option value="allProcess">All Process</option>
                </select>

                {autoMrpScope === "product" && (
                  <Select
                    options={productOptions}
                    value={autoMrpProduct}
                    onChange={setAutoMrpProduct}
                    placeholder="Search Product Code"
                    isClearable
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                )}

                {autoMrpScope === "process" && (
                  <Select
                    options={processOptions.map((p) => ({
                      value: p.process_id,
                      label: `${p.process_id} - ${p.process_name}`,
                    }))}
                    value={autoMrpProcess}
                    onChange={setAutoMrpProcess}
                    placeholder="Select Process"
                    isClearable
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                )}

                <div style={{ fontSize: 12, color: "#666", marginTop: 8 }}>
                  {autoMrpScope === "process"
                    ? "Runs MRP for every product code linked to this process (bom.process_id) — one level per product, same as the Run MRP button, looped."
                    : autoMrpScope === "allProcess"
                      ? "Runs MRP for every process, one after another — each process covering every product code linked to it."
                      : "Explodes the BOM top-down all the way to raw materials — same netting logic as Run MRP, just chained through every level."}
                </div>
              </div>

              {mrpSchedules.length > 0 && (
                <div style={{ marginBottom: "15px", borderTop: "1px solid #eee", paddingTop: "12px" }}>
                  <label style={{ display: "block", fontWeight: "bold", marginBottom: "8px" }}>
                    Active Schedules:
                  </label>
                  <div style={{ maxHeight: 140, overflowY: "auto" }}>
                    {mrpSchedules.map((s) => (
                      <div
                        key={s.id}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          fontSize: 13,
                          padding: "6px 0",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        <span>
                          <strong>
                            {s.scope === "product"
                              ? s.product_code
                              : s.scope === "process"
                                ? `Process: ${s.process_id}`
                                : s.scope === "allProcess"
                                  ? "All Processes"
                                  : "All FG"}
                          </strong>
                          {" — "}
                          {s.frequency}
                          {s.frequency === "hourly" && ` @ :${s.minute_of_hour}`}
                          {s.frequency === "daily" && ` @ ${s.time_of_day}`}
                          {s.frequency === "weekly" &&
                            ` @ ${["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][s.day_of_week]} ${s.time_of_day}`}
                          {s.frequency === "monthly" && ` @ day ${s.day_of_month} ${s.time_of_day}`}
                          {s.last_run_at && (
                            <span style={{ color: "#888" }}>
                              {" "}(last run: {new Date(s.last_run_at).toLocaleString("en-IN")}, {s.last_run_status})
                            </span>
                          )}
                        </span>
                        <button
                          onClick={() => handleDeleteSchedule(s.id)}
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "#d32f2f",
                            cursor: "pointer",
                            fontWeight: "bold",
                          }}
                          title="Delete schedule"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button
                  onClick={() => setScheduleModal(false)}
                  style={{ padding: "8px 15px", border: "1px solid #ccc", background: "#f5f5f5", cursor: "pointer" }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveSchedule}
                  disabled={autoMrpRunning}
                  style={{
                    padding: "8px 15px",
                    border: "none",
                    background: "#1976d2",
                    color: "#fff",
                    cursor: autoMrpRunning ? "not-allowed" : "pointer",
                    opacity: autoMrpRunning ? 0.7 : 1,
                  }}
                >
                  {autoMrpRunning
                    ? "RUNNING..."
                    : autoMrpFrequency === "immediate"
                      ? "Run Now"
                      : "Save Schedule"}
                </button>
                <button
                  onClick={async () => {
                    // Modal-only pegging button: run pegging, and only
                    // close the schedule modal once it actually succeeds.
                    const ok = await handleRunPegging();
                    if (ok) setScheduleModal(false);
                  }}
                  disabled={peggingRunning}
                  style={{
                    padding: "8px 15px",
                    border: "none",
                    background: "#1976d2",
                    color: "#fff",
                    cursor: peggingRunning ? "not-allowed" : "pointer",
                    opacity: peggingRunning ? 0.7 : 1,
                  }}
                >
                  {peggingRunning ? "RUNNING..." : "PEGGING"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* SALES ORDER CHARACTERISTICS */}
        <CharacteristicsModal
          modalVisible={modalVisible}
          selectedCharacteristics={selectedCharacteristics}
          handleClose={() => setModalVisible(false)}
          isEditing={false}
          handleChange={() => { }}
        />

      </div>
    </Layout>
  );
}

export default ProductPlanning;